import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../../context/StoreContext";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Modal from "../../components/common/Modal";
import NumericKeypadModal from "../../components/common/NumericKeypadModal";
import {
  useGetOpenTillSessionsQuery,
  useGetReconciliationQuery,
  useGetTillSessionQuery,
  useGetTillsQuery,
  useCloseTillSessionMutation,
  useSubmitCashCountMutation,
  api,
} from "../../store/api";
import { usePosRealtime } from "../../hooks/usePosRealtime";
import { useDeviceCashSessionResolution } from "../../hooks/useDeviceCashSession";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  formatCurrency,
  getTenderSummaryAmount,
  normalizeMoney,
} from "../../utils/cashingUp";
import { useCurrentOperator } from "../../hooks/useCurrentOperator";
import { logout } from "../../store/authSlice";

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const getCashCountCacheKey = (cashSessionId) =>
  `pos.cash-up.cash-count.${cashSessionId || "unknown"}`;

const parseMoney = (value) => {
  return normalizeMoney(value);
};

const formatShortDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const formatVarianceLabel = (value) => {
  if (!Number.isFinite(value) || value === 0) return formatCurrency(0);
  const formatted = formatCurrency(Math.abs(value));
  return value < 0 ? `${formatted} shortage` : `${formatted} overage`;
};

const cashUpControlClassName =
  "flex h-11 w-full items-center rounded-xl border border-slate-200 bg-white px-4 text-base text-slate-900 shadow-sm outline-none transition focus:border-slate-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:focus:border-slate-500";

const SummaryLine = ({ label, value, helperText = "", tone = "neutral" }) => {
  const tones = {
    neutral: "text-slate-900 dark:text-slate-100",
    success: "text-emerald-700 dark:text-emerald-300",
    danger: "text-rose-600 dark:text-rose-300",
    info: "text-indigo-700 dark:text-indigo-300",
  };

  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-200 py-3 last:border-b-0 dark:border-slate-700">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
          <span>{label}</span>
          {helperText ? (
            <span className="ml-1 text-[11px] font-normal normal-case tracking-normal text-slate-400 dark:text-slate-500">
              ({helperText})
            </span>
          ) : null}
        </p>
      </div>
      <p className={`text-right text-sm font-semibold ${tones[tone] || tones.neutral}`}>
        {value}
      </p>
    </div>
  );
};

const EditableSummaryLine = ({
  label,
  helper,
  value,
  onFocus,
  onChange,
  tone = "neutral",
}) => {
  const tones = {
    neutral: "text-slate-900 dark:text-slate-100",
    success: "text-emerald-700 dark:text-emerald-300",
    danger: "text-rose-600 dark:text-rose-300",
    info: "text-indigo-700 dark:text-indigo-300",
  };

  return (
    <div className="grid gap-3 border-b border-slate-200 py-3 last:border-b-0 dark:border-slate-700 sm:grid-cols-[minmax(0,1fr)_minmax(160px,220px)] sm:items-center">
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
          {label}
        </p>
        {helper ? (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{helper}</p>
        ) : null}
      </div>
      <input
        className={[
          cashUpControlClassName,
          "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          "h-10 text-right font-semibold",
          tones[tone] || tones.neutral,
        ].join(" ")}
        type="number"
        min="0"
        step="0.01"
        value={value}
        onFocus={onFocus}
        onChange={onChange}
      />
    </div>
  );
};

const CashUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedStoreId } = useStore();
  const { operatorName, userId } = useCurrentOperator();
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [cardMachineTotal, setCardMachineTotal] = useState("");
  const [countedCash, setCountedCash] = useState("");
  const [varianceNote, setVarianceNote] = useState("");
  const [draftMessage, setDraftMessage] = useState("");
  const [localError, setLocalError] = useState("");
  const [cashUpSubmitted, setCashUpSubmitted] = useState(false);
  const [numericPadField, setNumericPadField] = useState(null);
  const [postSubmitConfirmOpen, setPostSubmitConfirmOpen] = useState(false);
  const [closeSessionModalOpen, setCloseSessionModalOpen] = useState(false);
  const [closeSessionConfirmOpen, setCloseSessionConfirmOpen] = useState(false);
  const [closeSessionAt, setCloseSessionAt] = useState("");
  const [closeSessionError, setCloseSessionError] = useState("");
  const [postSubmitModalVariant, setPostSubmitModalVariant] = useState("info");
  const [submitCashCountRequested, setSubmitCashCountRequested] = useState(false);
  const hydratedSessionRef = useRef(null);
  const countedCashTouchedRef = useRef(false);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const {
    data: openSessionsData,
    isLoading: openSessionsLoading,
    isError: openSessionsIsError,
    error: openSessionsError,
    refetch: refetchOpenSessions,
  } = useGetOpenTillSessionsQuery(selectedStoreId, { skip: !selectedStoreId });

  const openSessions = useMemo(
    () => (Array.isArray(openSessionsData) ? openSessionsData : []),
    [openSessionsData],
  );

  const deviceSessionResolution = useDeviceCashSessionResolution(
    openSessions,
    Boolean(selectedStoreId),
  );

  useEffect(() => {
    if (!selectedStoreId) {
      setSelectedSessionId("");
      return;
    }

    if (openSessionsLoading || deviceSessionResolution.isLoading) {
      return;
    }

    if (deviceSessionResolution.status === "ready") {
      setSelectedSessionId(deviceSessionResolution.selectedSessionId);
      return;
    }

    setSelectedSessionId("");
  }, [
    deviceSessionResolution.isLoading,
    deviceSessionResolution.selectedSessionId,
    deviceSessionResolution.status,
    openSessionsLoading,
    selectedStoreId,
  ]);

  const {
    data: sessionData,
    isError: sessionIsError,
    error: sessionError,
    refetch: refetchSession,
  } = useGetTillSessionQuery(selectedSessionId, { skip: !selectedSessionId });

  const session = sessionData && typeof sessionData === "object" ? sessionData : null;

  const {
    data: reconciliationData,
    isError: reconciliationIsError,
    error: reconciliationError,
  } = useGetReconciliationQuery(selectedSessionId, { skip: !selectedSessionId });

  const { data: tillsData } = useGetTillsQuery(
    { storeId: session?.storeId || selectedStoreId, activeOnly: true },
    { skip: !selectedStoreId && !session?.storeId },
  );

  const tills = useMemo(
    () => (Array.isArray(tillsData) ? tillsData : []),
    [tillsData],
  );
  const tillById = useMemo(
    () => new Map(tills.map((till) => [till.tillId, till])),
    [tills],
  );
  const tillDisplay = useMemo(() => {
    if (deviceSessionResolution.status === "no_session") {
      return {
        title: "No active session",
        subtitle: "",
      };
    }

    if (!selectedSessionId) {
      return {
        title: "Session unavailable",
        subtitle: deviceSessionResolution.message || "Select a valid session to continue.",
      };
    }

    const till = tillById.get(session?.tillId);
    return {
      title:
        till?.tillName ||
        till?.tillCode ||
        session?.tillName ||
        session?.tillCode ||
        "Till unavailable",
      subtitle: "",
    };
  }, [
    deviceSessionResolution.message,
    deviceSessionResolution.status,
    selectedSessionId,
    session,
    tillById,
  ]);

  const [closeTillSession, closeTillSessionState] =
    useCloseTillSessionMutation();

  const status = session?.status || "OPEN";
  const normalizedStatus = String(status || "").toUpperCase();

  const openingCash = normalizeMoney(
    reconciliationData?.expectedCash?.openingCash || session?.openingCash || 0,
  );
  const cashSales = normalizeMoney(reconciliationData?.expectedCash?.cashPayments || 0);
  const systemCardSales = normalizeMoney(
    session?.cardSales || getTenderSummaryAmount(reconciliationData, "CARD"),
  );
  const systemCardTips = normalizeMoney(
    session?.cardTip || 
      (reconciliationData?.expectedCash?.cardTips ??
        reconciliationData?.expectedCash?.tipSales ??
        0),
  );
  const cashRefunds = normalizeMoney(
    session?.cashRefunds ?? reconciliationData?.expectedCash?.cashRefunds ?? 0,
  );
  const cardRefunds = normalizeMoney(
    session?.cardRefunds ?? reconciliationData?.expectedCash?.cardRefunds ?? 0,
  );
  const paidOut = normalizeMoney(reconciliationData?.expectedCash?.paidOut || 0);
  const cashDrop = normalizeMoney(reconciliationData?.expectedCash?.cashDrop || 0);
  const tipPayout = normalizeMoney(reconciliationData?.expectedCash?.tipPayout || 0);
  const recordedMovements = normalizeMoney(paidOut + cashDrop + tipPayout);
  const cashTakenFromDrawer = recordedMovements;

  const cardMachineTotalValue = parseMoney(cardMachineTotal);
  const countedCashValue = parseMoney(countedCash);
  const expectedFinalCash = normalizeMoney(
    openingCash + cashSales - cashRefunds - cashTakenFromDrawer,
  );
  const expectedCardTotal = normalizeMoney(
    systemCardSales + systemCardTips - cardRefunds,
  );
  const cardVariance = normalizeMoney(cardMachineTotalValue - expectedCardTotal);
  const cashVariance = normalizeMoney(countedCashValue - expectedFinalCash);
  const overallVariance = normalizeMoney(cashVariance + cardVariance);
  const cashVarianceAmount = Math.abs(cashVariance);
  const overallVarianceAmount = Math.abs(overallVariance);
  const cashVarianceLabel = formatVarianceLabel(cashVariance);
  const isCashShortage = cashVariance < 0;
  const isCashOverage = cashVariance > 0;
  const isCardShortage = cardVariance < 0;
  const isCardOverage = cardVariance > 0;
  const hasCashVariance = cashVariance !== 0;
  const hasCardVariance = cardVariance !== 0;
  const hasAnyVariance = hasCashVariance || hasCardVariance;

  const submittedCountJson = useMemo(() => {
    try {
      return session?.closingCountJson ? JSON.parse(session.closingCountJson) : {};
    } catch {
      return {};
    }
  }, [session?.closingCountJson]);

  useEffect(() => {
    if (!selectedSessionId || !session) return;
    if (hydratedSessionRef.current === selectedSessionId) return;

    const cached = submittedCountJson || {};

    const nextCardMachineTotal =
      cached.cardMachineTotal ?? cached.cardTotal ?? expectedCardTotal;
    const nextCountedCash =
      cached.countedCash ??
      cached.totalCash ??
      cached.actualCash ??
      expectedFinalCash;

    setCardMachineTotal(String(nextCardMachineTotal ?? ""));
    setCountedCash(String(nextCountedCash ?? ""));
    setVarianceNote(cached.varianceNote || "");

    hydratedSessionRef.current = selectedSessionId;
  }, [
    expectedFinalCash,
    expectedCardTotal,
    selectedSessionId,
    session,
    submittedCountJson,
  ]);

  useEffect(() => {
    setCashUpSubmitted(false);
    setDraftMessage("");
    setLocalError("");
    countedCashTouchedRef.current = false;
  }, [selectedSessionId]);

  const cashUpCacheKey = useMemo(
    () => getCashCountCacheKey(selectedSessionId),
    [selectedSessionId],
  );

  const getCachedDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(cashUpCacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [cashUpCacheKey]);

  useEffect(() => {
    if (!selectedSessionId || cashUpSubmitted) return;

    const cached = getCachedDraft();
    if (!cached) {
      if (!countedCashTouchedRef.current) {
        setCountedCash(String(expectedFinalCash));
      }
      return;
    }

    if (cached.cardMachineTotal !== undefined) {
      setCardMachineTotal(String(cached.cardMachineTotal));
    }
    const cachedExpectedFinalCash = parseMoney(cached.expectedFinalCash);
    const cachedCountedCash = cached.countedCash;
    const shouldRestoreCountedCash =
      cachedCountedCash !== undefined &&
      (cached.expectedFinalCash === undefined ||
        Math.abs(cachedExpectedFinalCash - expectedFinalCash) < 0.01);

    if (shouldRestoreCountedCash) {
      setCountedCash(String(cachedCountedCash));
    } else if (!countedCashTouchedRef.current) {
      setCountedCash(String(expectedFinalCash));
    }
    if (cached.varianceNote !== undefined) {
      setVarianceNote(cached.varianceNote || "");
    }
  }, [cashUpSubmitted, expectedFinalCash, getCachedDraft, selectedSessionId]);

  const handleSessionChange = useCallback(
    (payload) => {
      const changedSessionId = payload?.cashSessionId || payload?.sessionId;
      if (!changedSessionId || changedSessionId === selectedSessionId) {
        refetchSession();
      }
      refetchOpenSessions();
    },
    [refetchOpenSessions, refetchSession, selectedSessionId],
  );

  usePosRealtime({
    onCashSessionChanged: handleSessionChange,
  });

  const [submitCashCount, submitCashCountState] = useSubmitCashCountMutation();

  const canSubmitCashUp =
    Boolean(selectedSessionId) &&
    !Number.isNaN(cardMachineTotalValue) &&
    !Number.isNaN(countedCashValue) &&
    normalizedStatus !== "CLOSED";

  const performSubmitCashUp = async () => {
    if (!selectedSessionId) {
      setLocalError("Select an active session first.");
      return false;
    }

    setLocalError("");
    setDraftMessage("");

    try {
      await submitCashCount({
        cashSessionId: selectedSessionId,
        payload: {
          submittedByUserId: null,
          actualCash: countedCashValue,
          closingCountJson: JSON.stringify({
            cardMachineTotal: cardMachineTotalValue,
            cashTakenFromDrawer,
            tipsTakenFromDrawer: cashTakenFromDrawer,
            cashRefunds,
            cardRefunds,
            countedCash: countedCashValue,
            expectedFinalCash,
            expectedCardTotal,
            cardVariance,
            cashVariance,
            varianceNote: varianceNote.trim() || null,
          }),
          notes: null,
        },
      }).unwrap();

      localStorage.setItem(
        cashUpCacheKey,
        JSON.stringify({
          cardMachineTotal: cardMachineTotalValue,
          countedCash: countedCashValue,
          expectedFinalCash,
          varianceNote: varianceNote.trim() || "",
        }),
      );

      setCashUpSubmitted(true);
      setDraftMessage("Cash up submitted.");
      refetchSession();
      refetchOpenSessions();
      return true;
    } catch (err) {
      setLocalError(getErrorMessage(err, "Failed to submit cash up."));
      return false;
    }
  };

  const submitCashUpAction = async () => {
    if (!selectedSessionId) {
      setLocalError("Select an active session first.");
      return;
    }

    if (!canSubmitCashUp) {
      setLocalError("Enter valid cash-up values before submitting.");
      return;
    }

    setLocalError("");
    setDraftMessage("");

    if (hasAnyVariance) {
      setPostSubmitModalVariant(isCashShortage || isCardShortage ? "warning" : "info");
      setSubmitCashCountRequested(true);
      setPostSubmitConfirmOpen(true);
      return;
    }

    setSubmitCashCountRequested(false);
    const submitted = await performSubmitCashUp();
    if (submitted) {
      openCloseSessionModal();
    }
  };

  const openCloseSessionModal = () => {
    if (!selectedSessionId || normalizedStatus === "CLOSED") return;
    setCloseSessionAt(new Date().toISOString());
    setCloseSessionError("");
    setCloseSessionConfirmOpen(false);
    setCloseSessionModalOpen(true);
  };

  const handleContinueToCloseSession = () => {
    setPostSubmitConfirmOpen(false);
    if (submitCashCountRequested) {
      void (async () => {
        const submitted = await performSubmitCashUp();
        setSubmitCashCountRequested(false);
        if (submitted) {
          openCloseSessionModal();
        }
      })();
      return;
    }
    openCloseSessionModal();
  };

  const requestCloseSessionConfirmation = () => {
    if (!selectedSessionId || normalizedStatus === "CLOSED") return;
    setCloseSessionError("");
    setCloseSessionConfirmOpen(true);
  };

  const confirmCloseSession = async () => {
    if (!selectedSessionId || normalizedStatus === "CLOSED") return;

    setCloseSessionError("");

    try {
      await closeTillSession({
        cashSessionId: selectedSessionId,
        payload: {
          closedByUserId: userId || null,
          notes: null,
        },
      }).unwrap();

      setCloseSessionConfirmOpen(false);
      setCloseSessionModalOpen(false);
      setDraftMessage("Session closed successfully.");
      setPostSubmitModalVariant("info");
      refetchSession();
      refetchOpenSessions();
      dispatch(logout());
      dispatch(api.util.resetApiState());
      navigate("/login", { replace: true });
    } catch (err) {
      setCloseSessionError(getErrorMessage(err, "Failed to close session."));
    }
  };

  const handleNumberChange = (setter) => (e) => {
    setter(e.target.value);
    if (setter === setCountedCash) {
      countedCashTouchedRef.current = true;
    }
    setCashUpSubmitted(false);
    setDraftMessage("");
    setLocalError("");
  };

  const numericPadFieldLabel = {
    cardMachineTotal: "PDQ Count",
    countedCash: "Counted Cash in Drawer",
  }[numericPadField];

  const numericPadValue =
    {
      cardMachineTotal,
      countedCash,
    }[numericPadField] ?? "";

  const handleNumericPadChange = (nextValue) => {
    const value = typeof nextValue === "string" ? nextValue : "";

    if (numericPadField === "cardMachineTotal") {
      setCardMachineTotal(value);
    } else if (numericPadField === "countedCash") {
      countedCashTouchedRef.current = true;
      setCountedCash(value);
    }

    setCashUpSubmitted(false);
    setDraftMessage("");
    setLocalError("");
  };

  return (
    <div className="space-y-4 px-4 pb-4 pt-2 md:px-6 md:pt-3 lg:px-8">
      {!selectedStoreId && (
        <Card className="border-slate-200 bg-white/95 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
          <Alert tone="warning">No store is selected. Choose a store first.</Alert>
        </Card>
      )}

      {selectedStoreId && (
        <div className="space-y-4">
          {openSessionsLoading && (
            <Alert tone="info">Loading open sessions...</Alert>
          )}
          {!openSessionsLoading &&
            !deviceSessionResolution.isLoading &&
            deviceSessionResolution.status !== "ready" &&
            deviceSessionResolution.status !== "no_session" && (
              <Alert tone="warning">{deviceSessionResolution.message}</Alert>
          )}
          {deviceSessionResolution.status === "no_session" && (
            <Alert tone="warning">{deviceSessionResolution.message}</Alert>
          )}
          {openSessionsIsError && (
            <Alert tone="error">
              {getErrorMessage(openSessionsError, "Failed to load open sessions.")}
            </Alert>
          )}
          {sessionIsError && (
            <Alert tone="error">
              {getErrorMessage(sessionError, "Failed to load session details.")}
            </Alert>
          )}
          {reconciliationIsError && (
            <Alert tone="error">
              {getErrorMessage(
                reconciliationError,
                "Failed to load cash-up summary.",
              )}
            </Alert>
          )}
          {localError && <Alert tone="error">{localError}</Alert>}
          {draftMessage && <Alert tone="info">{draftMessage}</Alert>}

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Review the totals below, enter the editable counts, and submit the shift cash-up.
              </p>
            </div>
            <div className="flex justify-start md:justify-end">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate("/pos/cash-movements")}
              >
                Back to Cash Movements
              </Button>
            </div>
          </div>

          <Card className="border-slate-200 bg-white/95 p-4 shadow-sm shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-950/70">
              <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 lg:flex-row lg:items-center lg:justify-between dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Cash Up Summary
                </h3>
                <div className="grid w-full gap-2 sm:grid-cols-2 lg:w-[520px] lg:flex-none">
                  <div className="flex h-[56px] items-center rounded-2xl border border-violet-200 bg-violet-50/70 px-3 shadow-sm dark:border-violet-900/40 dark:bg-violet-950/15">
                    <div className="min-w-0 flex w-full items-center justify-between gap-2 whitespace-nowrap leading-none">
                      <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-300">
                        Till
                      </p>
                      <p className="truncate text-[14px] font-semibold text-slate-900 dark:text-slate-100">
                        {tillDisplay.title}
                      </p>
                    </div>
                  </div>

                  <div className="flex h-[56px] items-center rounded-2xl border border-indigo-200 bg-indigo-50/70 px-3 shadow-sm dark:border-indigo-900/40 dark:bg-indigo-950/15">
                    <div className="min-w-0 flex w-full items-center justify-between gap-2 whitespace-nowrap leading-none">
                      <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-indigo-600 dark:text-indigo-300">
                        Shift
                      </p>
                      <p className="truncate text-[14px] font-semibold text-slate-900 dark:text-slate-100">
                        {formatShortDateTime(session?.openedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-4 xl:grid-cols-2">
                <div className="flex overflow-hidden rounded-2xl border border-emerald-200 bg-emerald-50/40 dark:border-emerald-900/40 dark:bg-emerald-950/10">
                  <div className="flex min-h-full w-full flex-col">
                  <div className="flex items-center gap-3 border-b border-emerald-200 px-4 py-3 dark:border-emerald-900/40">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                        <path
                          d="M4 7h16v10H4z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7 11h10"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        1. Cash Drawer Reconciliation
                      </h4>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col bg-white/90 px-4 dark:bg-slate-950/40">
                    <SummaryLine label="Starting Cash" value={formatCurrency(openingCash)} />
                    <SummaryLine label="Cash Sales" value={formatCurrency(cashSales)} />
                    <SummaryLine
                      label="Cash Refunds"
                      value={formatCurrency(cashRefunds)}
                      tone="danger"
                    />
                    <SummaryLine
                      label="Cash Removed From Drawer"
                      value={formatCurrency(cashTakenFromDrawer)}
                    />
                    <SummaryLine
                      label="Cash Left in Drawer"
                      value={formatCurrency(expectedFinalCash)}
                      tone="success"
                    />
                    <EditableSummaryLine
                      label="Counted Cash"
                      helper="Enter the final physical cash count."
                      value={countedCash}
                      onFocus={() => setNumericPadField("countedCash")}
                      onChange={handleNumberChange(setCountedCash)}
                      tone="success"
                    />
                    <div className="mt-auto border-t border-slate-200 dark:border-slate-700">
                      <SummaryLine
                        label="Variance"
                        value={formatVarianceLabel(cashVariance)}
                        tone={cashVariance === 0 ? "success" : "danger"}
                      />
                    </div>
                  </div>
                  </div>
                </div>

                <div className="flex overflow-hidden rounded-2xl border border-blue-200 bg-blue-50/40 dark:border-blue-900/40 dark:bg-blue-950/10">
                  <div className="flex min-h-full w-full flex-col">
                  <div className="flex items-center gap-3 border-b border-blue-200 px-4 py-3 dark:border-blue-900/40">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
                        <path
                          d="M7 5h10v14H7z"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 9h4M10 13h4"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        2. PDQ Reconciliation
                      </h4>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col bg-white/90 px-4 dark:bg-slate-950/40">
                    <SummaryLine label="PDQ Sales" value={formatCurrency(systemCardSales)} />
                    <SummaryLine
                      label="PDQ Refunds"
                      value={formatCurrency(cardRefunds)}
                      tone="danger"
                    />
                    {/*
                    <SummaryLine label="PDQ Tips" value={formatCurrency(systemCardTips)} />
                    */}
                    <SummaryLine
                      label="Expected PDQ Total"
                      value={formatCurrency(expectedCardTotal)}
                      tone="info"
                    />
                    <EditableSummaryLine
                      label="PDQ Count"
                      helper="Enter PDQ total."
                      value={cardMachineTotal}
                      onFocus={() => setNumericPadField("cardMachineTotal")}
                      onChange={handleNumberChange(setCardMachineTotal)}
                      tone="info"
                    />
                    <div className="mt-auto border-t border-slate-200 dark:border-slate-700">
                      <SummaryLine
                        label="Variance"
                        value={formatVarianceLabel(cardVariance)}
                        tone={cardVariance === 0 ? "success" : "danger"}
                      />
                    </div>
                  </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-sm shadow-slate-200/40 dark:border-slate-700 dark:bg-slate-950/70">
                <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 md:flex-row md:items-start md:justify-between dark:border-slate-700">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                      Overall Variance
                    </p>
                    <h4 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      Cash + PDQ
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Combined reconciliation result for this shift.
                    </p>
                  </div>

                  <div className="flex w-full justify-end md:max-w-[720px]">
                    <div className="min-w-[240px] rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900/40">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Overall Variance
                      </p>
                      <p
                        className={[
                          "mt-1 text-base font-semibold",
                          overallVariance === 0
                            ? "text-emerald-700 dark:text-emerald-300"
                            : overallVariance < 0
                              ? "text-rose-600 dark:text-rose-300"
                              : "text-amber-700 dark:text-amber-300",
                        ].join(" ")}
                      >
                        {formatCurrency(overallVarianceAmount)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                <Button
                  size="sm"
                  variant="pos-primary"
                  onClick={cashUpSubmitted ? openCloseSessionModal : submitCashUpAction}
                  disabled={
                    cashUpSubmitted
                      ? closeTillSessionState.isLoading || normalizedStatus === "CLOSED"
                      : !canSubmitCashUp || submitCashCountState.isLoading
                  }
                >
                  <span className="inline-flex items-center gap-2">
                    {cashUpSubmitted
                      ? "Close Session"
                      : submitCashCountState.isLoading
                        ? "Submitting..."
                        : "Submit Cash Up"}
                    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4">
                      <path
                        d="M5 12h14m-6-6l6 6-6 6"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Button>
              </div>

          </Card>
        </div>
      )}

      <NumericKeypadModal
        isOpen={Boolean(numericPadField)}
        title={numericPadFieldLabel || "Enter Value"}
        value={numericPadValue}
        onChange={handleNumericPadChange}
        onClose={() => setNumericPadField(null)}
      />

      <Modal
        isOpen={postSubmitConfirmOpen}
        onClose={() => {
          setPostSubmitConfirmOpen(false);
          setSubmitCashCountRequested(false);
        }}
        title={
          hasCashVariance && hasCardVariance
            ? "Cash and PDQ Variances Detected"
            : isCashShortage
              ? "Cash Shortage Detected"
              : isCashOverage
                ? "Cash Overage Detected"
                : isCardShortage
                  ? "PDQ Shortage Detected"
                  : isCardOverage
                    ? "PDQ Overage Detected"
                    : "Cash Up Ready"
        }
        confirmText={
          submitCashCountRequested ? "Submit Cash Up" : "Continue to Close Session"
        }
        cancelText="Review Again"
        onConfirm={handleContinueToCloseSession}
        size="md"
      >
        <div className="space-y-4">
          <Alert
            tone={
              postSubmitModalVariant === "warning"
                ? "warning"
                : postSubmitModalVariant === "success"
                  ? "success"
                  : "info"
            }
          >
            {hasCashVariance || hasCardVariance ? (
              <div className="space-y-1">
                {hasCashVariance && (
                  <p>{`Cash ${isCashShortage ? "shortage" : "overage"} detected: ${formatCurrency(cashVarianceAmount)}.`}</p>
                )}
                {hasCardVariance && (
                  <p>{`PDQ ${isCardShortage ? "shortage" : "overage"} detected: ${formatCurrency(Math.abs(cardVariance))}.`}</p>
                )}
              </div>
            ) : (
              "The cash-up submission has been saved successfully."
            )}
          </Alert>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
            <p className="font-semibold text-slate-900 dark:text-slate-100">
              {hasCashVariance || hasCardVariance
                ? "Review the variances, then submit the cash up."
                : "You can now continue to the session close confirmation."}
            </p>
            <p className="mt-2 leading-6">
              {hasCashVariance || hasCardVariance
                ? `Cash variance will be submitted as ${cashVarianceLabel}. PDQ variance will be submitted as ${formatVarianceLabel(cardVariance)}. You can proceed if this is expected.`
                : "Closing the session will lock the till and complete the cash-up flow."}
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={closeSessionModalOpen}
        onClose={() => {
          setCloseSessionConfirmOpen(false);
          setCloseSessionModalOpen(false);
          setCloseSessionError("");
        }}
        title="Close Session"
        confirmText="Close Session"
        onConfirm={requestCloseSessionConfirmation}
        isLoading={closeTillSessionState.isLoading}
        size="lg"
        align="center"
        cancelText="Cancel"
        showCancel
        showCloseButton
      >
        <div className="space-y-4">
          {closeSessionError && <Alert tone="error">{closeSessionError}</Alert>}

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Till
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {tillDisplay.title}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                User
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {operatorName}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Opening Time
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {formatShortDateTime(session?.openedAt)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Closing Time
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                {formatShortDateTime(closeSessionAt)}
              </p>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={closeSessionConfirmOpen}
        onClose={() => setCloseSessionConfirmOpen(false)}
        title="Confirm Close Session"
        confirmText={
          closeTillSessionState.isLoading ? "Closing..." : "Close Session"
        }
        cancelText="Cancel"
        onConfirm={confirmCloseSession}
        isLoading={closeTillSessionState.isLoading}
        size="md"
        align="center"
        zIndexClassName="z-[60]"
        showCancel
        showCloseButton
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-slate-600 dark:text-slate-400">
            Are you sure you want to close this session?
          </p>
          <Alert tone="warning">
            Closing this session will lock further POS activity until a new session is started. The session will close even if a variance was recorded.
          </Alert>
          {closeSessionError && <Alert tone="error">{closeSessionError}</Alert>}
        </div>
      </Modal>
    </div>
  );
};

export default CashUp;

